import { useState } from 'react';
import type { ReactNode } from 'react';
import { defaultId, flattenTree, nodeFlags, visibleWindow } from './tree';
import type { TreeNodeLike } from './tree';
import './tree.css';

export interface VirtualTreeProps<T extends TreeNodeLike<T>> {
  roots: readonly T[];
  expanded: ReadonlySet<string>;
  rowHeight?: number;
  viewportHeight?: number;
  overscan?: number;
  getId?: (node: T) => string;
  renderRow?: (node: T, level: number, expanded: boolean) => ReactNode;
  onToggle?: (id: string) => void;
  emptyLabel?: string;
}

const DEFAULTS = { rowHeight: 36, viewportHeight: 480, overscan: 3 };

/**
 * Renders the window of rows the viewport can show, not the whole chart.
 * `data-row-count` is the real flattened length, so a test can prove the DOM
 * stays small while the chart stays large.
 */
export function VirtualTree<T extends TreeNodeLike<T>>({
  roots,
  expanded,
  rowHeight = DEFAULTS.rowHeight,
  viewportHeight = DEFAULTS.viewportHeight,
  overscan = DEFAULTS.overscan,
  getId = defaultId as (node: T) => string,
  renderRow,
  onToggle,
  emptyLabel,
}: VirtualTreeProps<T>): JSX.Element {
  const [scrollTop, setScrollTop] = useState(0);
  const rows = flattenTree(roots, expanded, getId);
  const window = visibleWindow(rows.length, rowHeight, viewportHeight, overscan, scrollTop);
  const visible = rows.slice(window.startIndex, window.startIndex + window.rows);

  if (rows.length === 0) {
    return (
      <div className="vtree vtree--empty" data-row-count="0">
        {emptyLabel === undefined ? null : <p className="muted">{emptyLabel}</p>}
      </div>
    );
  }

  return (
    <div
      className="vtree"
      data-row-count={rows.length}
      style={{ height: viewportHeight }}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      <div className="vtree__canvas" style={{ height: window.totalHeight }}>
        <div className="vtree__window" style={{ transform: `translateY(${String(window.offsetY)}px)` }}>
          {visible.map((row) => {
            const id = getId(row.node);
            const isOpen = expanded.has(id);
            const flags = nodeFlags(row.node as { children?: unknown[]; isGroup?: boolean; isOffbalance?: boolean });
            return (
              <div
                key={id}
                data-testid="tree-row"
                data-code={id}
                data-level={row.level}
                data-group={flags.isGroup ? 'true' : undefined}
                data-offbalance={flags.isOffbalance ? 'true' : undefined}
                className="vtree__row"
                style={{ height: rowHeight }}
              >
                <span className="vtree__indent" style={{ width: row.level * 18 }} />
                {flags.hasChildren ? (
                  <button
                    type="button"
                    className="vtree__toggle"
                    data-testid="tree-toggle"
                    aria-expanded={isOpen}
                    onClick={() => onToggle?.(id)}
                  >
                    {isOpen ? '−' : '+'}
                  </button>
                ) : (
                  <span className="vtree__toggle vtree__toggle--leaf" />
                )}
                {renderRow === undefined ? (
                  <span className="vtree__label">{String((row.node as { name?: unknown }).name ?? id)}</span>
                ) : (
                  renderRow(row.node, row.level, isOpen)
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
